'use client';

import Script from 'next/script';
import { usePathname } from 'next/navigation';
import * as React from 'react';

/* Meta Pixel for the marketing site, loaded directly rather than through a GTM
   tag — the container is unconfigured and lib/marketing-events.ts calls fbq()
   itself for conversions.

   The id is a public identifier that ships in the HTML to every visitor, so
   there is nothing to protect. The CAPI token in lib/meta-capi.ts stays in env:
   it is secret, and route handlers read it per request.

   Without the CSP nonce the browser refuses this inline snippet. */
const PIXEL_ID = '866327436444517';

export function MetaPixel({ nonce }: { nonce?: string }) {
  const pathname = usePathname();
  const firstPath = React.useRef(true);

  /* The init snippet fires the first PageView; client-side navigations between
     landing routes never reload it, so re-fire one per pathname change. */
  React.useEffect(() => {
    if (firstPath.current) {
      firstPath.current = false;
      return;
    }
    const w = window as typeof window & { fbq?: (...args: unknown[]) => void };
    w.fbq?.('track', 'PageView');
  }, [pathname]);

  /* No <noscript> fallback: React renders noscript children as live DOM, so the
     tracking pixel fires alongside the script one and double-counts PageView.
     Nothing on this site works without JS anyway. */
  return (
    <Script id="cultiv-meta-pixel" strategy="afterInteractive" nonce={nonce}>
      {`!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window,document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init','${PIXEL_ID}');
fbq('track','PageView');`}
    </Script>
  );
}
